/*
 * WorkAndBuhin.vue の選択部品リスト構築、一括選択、おすすめ部品選択同期を切り出した処理。
 */
import { BuhinDetail } from "@/model/Buhin";
import { toArray } from "@/components/work-buhin/WorkBuhinAdapter";
import { INSPECT_TYPE } from "@/model/InspectType";
import { STATUS_SELECT_WORK_BUHIN } from "@/model/StatusSelectWorkBuhin";

export interface WorkBuhinSelectionOptions {
  isRecommend: boolean;
}

export interface WorkBuhinSelectionState {
  workBuhinList: BuhinDetail[];
  osusumeBuhinList: BuhinDetail[];
  manualBuhinList: BuhinDetail[];
  selectedBuhinList: BuhinDetail[];
  activeSeibiBuhinList: BuhinDetail[];
  freeOfChargeBuhinList: BuhinDetail[];
}

export interface SelectAllBuhinState {
  shouldApply: boolean;
  buhinContent: any[];
  osusumeBuhinList: BuhinDetail[];
  selectedBuhinList: BuhinDetail[];
}

export interface SelectAllBuhinApplyEffectState {
  shouldApply: boolean;
  buhinContent: any[];
  osusumeBuhinList: BuhinDetail[];
  selectedBuhinList: BuhinDetail[];
  shouldCheckDisabledBtnConfirm: boolean;
}

export interface RecommendBuhinSelectedState {
  shouldApply: boolean;
  isSelected: boolean;
}

export interface RecommendBuhinSelectedApplyEffectState {
  shouldApply: boolean;
  isSelected: boolean;
}

/*
 * buildWorkBuhinSelectionState を WorkAndBuhin.vue から分離し、
 * 部品選択状態 を親側置換や単体テストで固定するため。
 */
export function buildWorkBuhinSelectionState(
  work: any,
  options: WorkBuhinSelectionOptions
): WorkBuhinSelectionState {
  const workBuhinList = removeDuplicateBuhinByCd(toArray<BuhinDetail>(work && work.buhinList));
  const osusumeBuhinList = buildOsusumeBuhinList(work, options);
  const manualBuhinList = toArray<BuhinDetail>(work && work.manualBuhinList);
  const freeOfChargeBuhinList = toArray<BuhinDetail>(work && work.freeOfChargeBuhinList)
    .filter((buhin: BuhinDetail | null) => buhin != null) as BuhinDetail[];
  let activeSeibiBuhinList = deepCopy(workBuhinList);

  activeSeibiBuhinList = activeSeibiBuhinList.filter(
    (buhin: BuhinDetail) =>
      buhin.isDelete != 1 &&
      (buhin.quantity > 0 || buhin.isExtreme === 1 || buhin.isExtreme === 2)
  );

  if (options.isRecommend) {
    const selectedOsusumeBuhinList = osusumeBuhinList.filter(
      (buhin: BuhinDetail) => buhin.isDelete != 1 && buhin.id != null
    );
    if (selectedOsusumeBuhinList.length > 0) {
      activeSeibiBuhinList = [
        ...activeSeibiBuhinList,
        ...selectedOsusumeBuhinList
      ];
    }
  }

  const selectedBuhinList = work && work.isExtreme == 0
    ? activeSeibiBuhinList
    : activeSeibiBuhinList.filter((buhin: BuhinDetail) => buhin.quantity > 0);

  manualBuhinList
    .filter((buhin: BuhinDetail) => buhin.isDelete != 1 && buhin.quantity > 0)
    .forEach((buhin: BuhinDetail) => {
      selectedBuhinList.push(deepCopy(buhin));
    });

  return {
    workBuhinList,
    osusumeBuhinList,
    manualBuhinList,
    selectedBuhinList,
    activeSeibiBuhinList,
    freeOfChargeBuhinList
  };
}

/*
 * buildSelectAllBuhinState を WorkAndBuhin.vue から分離し、
 * 部品選択状態 を親側置換や単体テストで固定するため。
 */
export function buildSelectAllBuhinState(params: {
  data: any;
  work: any;
  buhinContent: any[];
  osusumeBuhinList: BuhinDetail[];
}): SelectAllBuhinState {
  if (!params.work || toArray(params.work.osusumeBuhinList).length === 0) {
    return {
      shouldApply: false,
      buhinContent: params.buhinContent,
      osusumeBuhinList: params.osusumeBuhinList,
      selectedBuhinList: []
    };
  }

  const isRecommend = isRecommendInspectType(resolveSelectAllInspectType(params.data));
  const buhinContent = toArray<any>(params.buhinContent).map((category: any, index: number) => {
    if (index !== 0) {
      return { ...category };
    }
    return {
      ...category,
      items: toArray<any>(category && category.items).map((buhin: any) => {
        const selected = {
          ...buhin,
          quantity: 1
        };
        if (isRecommend) {
          return {
            ...selected,
            isSelected: true
          };
        }
        return selected;
      })
    };
  });
  const firstItems = buhinContent[0] ? toArray<BuhinDetail>(buhinContent[0].items) : [];

  return {
    shouldApply: true,
    buhinContent,
    osusumeBuhinList: isRecommend
      ? toArray<BuhinDetail>(params.osusumeBuhinList).map((buhin: BuhinDetail) => ({
        ...buhin,
        quantity: 1,
        isSelected: true
      } as any))
      : toArray<BuhinDetail>(params.osusumeBuhinList),
    selectedBuhinList: deepCopy(firstItems)
  };
}

/*
 * buildSelectAllBuhinApplyEffectState を WorkAndBuhin.vue から分離し、
 * 一括選択後の部品表示、選択 list、confirm button 再判定条件を固定するため。
 */
export function buildSelectAllBuhinApplyEffectState(
  selectAllState: SelectAllBuhinState
): SelectAllBuhinApplyEffectState {
  return {
    shouldApply: selectAllState.shouldApply,
    buhinContent: selectAllState.buhinContent,
    osusumeBuhinList: selectAllState.osusumeBuhinList,
    selectedBuhinList: selectAllState.selectedBuhinList,
    shouldCheckDisabledBtnConfirm: selectAllState.shouldApply
  };
}

/*
 * buildRecommendBuhinSelectedState を WorkAndBuhin.vue から分離し、
 * 部品選択状態 を親側置換や単体テストで固定するため。
 */
export function buildRecommendBuhinSelectedState(
  data: any,
  isSelected: boolean
): RecommendBuhinSelectedState {
  return {
    shouldApply: isRecommendInspectType(data && data.sknInspectType),
    isSelected
  };
}

/*
 * buildRecommendBuhinSelectedApplyEffectState を WorkAndBuhin.vue から分離し、
 * おすすめ部品の isSelected を component 側で反映する条件と値を固定するため。
 */
export function buildRecommendBuhinSelectedApplyEffectState(
  selectedState: RecommendBuhinSelectedState
): RecommendBuhinSelectedApplyEffectState {
  return {
    shouldApply: selectedState.shouldApply,
    isSelected: selectedState.isSelected
  };
}

/*
 * removeDuplicateBuhinByCd を WorkAndBuhin.vue から分離し、
 * 部品選択状態 を親側置換や単体テストで固定するため。
 */
export function removeDuplicateBuhinByCd(buhinList: BuhinDetail[]): BuhinDetail[] {
  const listCd: Array<string | null> = [];
  return buhinList.filter((buhin: BuhinDetail) => {
    if (listCd.indexOf(buhin.buhinCd) > -1) {
      return false;
    }
    listCd.push(buhin.buhinCd);
    return true;
  });
}

function resolveSelectAllInspectType(data: any): number {
  if (data && data.sknInspectType) {
    return data.sknInspectType;
  }
  if (data && data.seibiList && data.seibiList.length > 0) {
    return data.seibiList[0].sknInspectType;
  }
  return 99;
}

function isRecommendInspectType(sknInspectType: number): boolean {
  return (
    sknInspectType == INSPECT_TYPE.RECOMMEND ||
    sknInspectType == INSPECT_TYPE.RECOMMEND_MANUAL
  );
}

function buildOsusumeBuhinList(
  work: any,
  options: WorkBuhinSelectionOptions
): BuhinDetail[] {
  const osusumeBuhinList = toArray<BuhinDetail>(work && work.osusumeBuhinList);
  if (!options.isRecommend) {
    return osusumeBuhinList;
  }
  const selectedOsusumeBuhinList = osusumeBuhinList.filter(
    (buhin: BuhinDetail) => buhin.isDelete != 1 && buhin.id != null
  );
  // 初回のみ候補を自動選択する。部品なしで確定済みの作業は再選択しない。
  const isInitial = work.buhinSelected == null ||
    work.buhinSelected == STATUS_SELECT_WORK_BUHIN.Initial;
  if (isInitial && selectedOsusumeBuhinList.length === 0 && osusumeBuhinList.length === 1) {
    return [
      {
        ...osusumeBuhinList[0],
        isSelected: true
      } as any
    ];
  }
  return osusumeBuhinList;
}

function deepCopy<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}
